import { Controller } from "@hotwired/stimulus";
import { useStore } from "stimulus-store";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { getCsrfToken } from "shared/form_helpers";
import { allBundlesStore } from "../../../stores/device_types/all_bundles_store";
import { bundleStore } from "../../../stores/device_types/bundle_store";

// Connects to data-controller="forms--bundle--update-bundle-form"
export default class extends Controller {

    static stores = [
        allBundlesStore,
        bundleStore
    ]

    static targets = [
        'form',
        'bundleSelect',
        'name',
        'icon',
        'models',
        'parts',
        'submitButton'
    ];

    connect() {
        useStore(this);

        // Prevent default form submission
        if (this.hasFormTarget) {
            this.formTarget.addEventListener('submit', (e) => e.preventDefault());
        }
    }

    gatherFormData() {
        const formData = {
            id: this.bundleStoreValue?.id,
            name: this.nameTarget.value,
            models: this.modelsTarget.tomselect ? this.modelsTarget.tomselect.items : [],
            parts: this.partsTarget.tomselect ? this.partsTarget.tomselect.items : []
        };

        if (this.hasIconTarget) {
            formData.icon = this.iconTarget.value;
        }

        return formData;
    }

    async performUpdate(formData) {
        this.submitButtonTarget.disabled = true;

        try {
            const response = await fetch('/api/device_types/bundle/update', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                if (result.data.versioned) {
                    sendSuccessNotification(`New version created: ${result.data.name}`);
                } else if (result.data.task_id) {
                    sendSuccessNotification('Bundle updated. Existing instances are being updated.');
                } else {
                    sendSuccessNotification('Bundle updated successfully!');
                }

                // Update bundle in the store
                const updatedBundle = result.data;
                const currentBundles = this.allBundlesStoreValue || [];

                if (result.data.versioned) {
                    // Update the original bundle's name to its new timestamped value, then add the new bundle
                    const originalBundle = result.data.original_bundle;
                    const updatedBundles = currentBundles.map(bundle =>
                        bundle.id === originalBundle.id ? { ...bundle, name: originalBundle.name } : bundle
                    );
                    this.setAllBundlesStoreValue([...updatedBundles, updatedBundle]);
                } else {
                    const updatedBundles = currentBundles.map(bundle =>
                        bundle.id === updatedBundle.id ? updatedBundle : bundle
                    );
                    this.setAllBundlesStoreValue(updatedBundles);
                }

                // Clear the current bundle store
                this.setBundleStoreValue({});

                // Clear the form
                this.clearForm();
            } else {
                const errorMessage = result.message || 'Failed to update bundle. Please try again.';
                sendFailureNotification(errorMessage);
            }
        } catch (error) {
            console.error('Error updating bundle:', error);
            sendFailureNotification('An error occurred while updating the bundle. Please try again.');
        } finally {
            this.submitButtonTarget.disabled = false;
        }
    }

    async submit(event) {
        event.preventDefault();

        const bundleId = this.bundleStoreValue?.id;
        if (!bundleId) {
            sendFailureNotification('Please select a bundle to update.');
            return;
        }

        const modal = new bootstrap.Modal(document.getElementById('bundlePropagationModal'));
        modal.show();
    }

    submitWithPropagation() {
        const modal = bootstrap.Modal.getInstance(document.getElementById('bundlePropagationModal'));
        if (modal) {
            modal.hide();
        }

        this.performUpdate(this.gatherFormData());
    }

    submitWithVersioning() {
        const modal = bootstrap.Modal.getInstance(document.getElementById('bundlePropagationModal'));
        if (modal) {
            modal.hide();
        }

        const formData = this.gatherFormData();
        formData.version_existing_bundles = true;
        this.performUpdate(formData);
    }

    cancelPropagation() {
        const modal = bootstrap.Modal.getInstance(document.getElementById('bundlePropagationModal'));
        if (modal) {
            modal.hide();
        }
    }

    clearForm() {
        // Clear bundle select
        if (this.hasBundleSelectTarget && this.bundleSelectTarget.tomselect) {
            this.bundleSelectTarget.tomselect.clear();
        }

        // Clear text inputs
        this.nameTarget.value = '';
        this.nameTarget.disabled = true;

        // Clear icon select (uses fontIconPicker, not tomselect)
        if (this.hasIconTarget && typeof $.fn.fontIconPicker === 'function') {
            $(this.iconTarget).fontIconPicker().setIcon('');
            this.iconTarget.disabled = true;
        }

        // Clear models select
        if (this.modelsTarget.tomselect) {
            this.modelsTarget.tomselect.clear();
            this.modelsTarget.tomselect.disable();
        }

        // Clear parts select
        if (this.partsTarget.tomselect) {
            this.partsTarget.tomselect.clear();
            this.partsTarget.tomselect.disable();
        }
    }
}
